import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight, Download, Menu, X } from 'lucide-react';
import { PORTFOLIO } from '@/constants/testIds';
import { LogoMark } from './LogoMark';

// Links — `about` is now a real page, others are anchors on the home route
const links = [
    { id: 'projects', label: 'Projects', testId: 'nav-link-projects', kind: 'route', to: '/work' },
    { id: 'about', label: 'About', testId: PORTFOLIO.navLinkAbout, kind: 'route', to: '/about' },
    { id: 'stories', label: 'Stories', testId: 'nav-link-stories', kind: 'route', to: '/stories' },
    { id: 'philosophy', label: 'Philosophy', testId: PORTFOLIO.navLinkPhilosophy, kind: 'anchor' },
    { id: 'contact', label: 'Contact', testId: PORTFOLIO.navLinkContact, kind: 'anchor' },
];

// Small hook — fetches the active resume once. Returns `null` until settled
// so the nav CTA doesn't flash for visitors who don't have one uploaded.
const useActiveResume = () => {
    const [resume, setResume] = useState(null);
    useEffect(() => {
        let alive = true;
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resume`)
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (alive && data && data.url) setResume(data);
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, []);
    return resume;
};

const Navigation = () => {
    const [scrolled, setScrolled] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const onHome = location.pathname === '/';
    const anchorHref = (id) => (onHome ? `#${id}` : `/#${id}`);
    const resume = useActiveResume();
    const resumeHref = resume
        ? `${process.env.REACT_APP_BACKEND_URL}${resume.url}`
        : null;

    useEffect(() => {
        let lastY = window.scrollY;
        const onScroll = () => {
            const y = window.scrollY;
            setScrolled(y > 32);
            const diff = y - lastY;
            // Always reveal near the top; hide on scroll down, show on scroll up.
            if (y < 80) setHidden(false);
            else if (diff > 6) setHidden(true);
            else if (diff < -6) setHidden(false);
            lastY = y;
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Close drawer on route change
    useEffect(() => {
        setMobileOpen(false);
    }, [location]);

    // Lock body scroll while drawer open
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileOpen]);

    const renderLink = (l, className, onClick) =>
        l.kind === 'route' ? (
            <Link
                key={l.id}
                to={l.to}
                data-testid={l.testId}
                data-cursor="hover"
                onClick={onClick}
                className={className}
            >
                {l.label}
            </Link>
        ) : (
            <a
                key={l.id}
                href={anchorHref(l.id)}
                data-testid={l.testId}
                data-cursor="hover"
                onClick={onClick}
                className={className}
            >
                {l.label}
            </a>
        );

    return (
        <>
            <motion.header
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: hidden && !mobileOpen ? -160 : 0, opacity: 1 }}
                transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
                // On mobile, float pinned at top-left so it mirrors the
                // theme-toggle pinned at top-right (same 36 px height).
                // On desktop, revert to the centered nav bar.
                className="fixed top-5 md:top-5 left-5 right-auto md:left-0 md:right-0 z-50 flex md:justify-center"
            >
                <div
                    className={`rounded-full transition-all duration-500 border shadow-[0_8px_24px_rgba(0,0,0,0.35)] ${
                        scrolled
                            ? 'bg-[var(--surface)]/95 backdrop-blur-xl border-[var(--line-2)]'
                            : 'bg-[var(--surface)]/85 backdrop-blur-md border-[var(--line)]'
                    }`}
                >
                    {/* Mobile: h-9 shell aligning with theme toggle. Desktop
                        keeps its taller padded shell. */}
                    <div className="flex items-center h-9 gap-1 pl-1 pr-1 md:h-auto md:gap-3 md:pl-3 md:pr-2 md:py-1.5">
                        <Link
                            to="/"
                            data-testid={PORTFOLIO.navLogo}
                            data-cursor="hover"
                            // Mobile: compact monogram-only link that fits inside h-9.
                            // Desktop: full "Himanshu /T" wordmark chip.
                            className="group flex items-center gap-2 h-7 md:h-auto px-2 md:pl-2 md:pr-3 md:py-1.5 rounded-full hover:bg-white/5 transition-colors"
                        >
                            <LogoMark size={34} className="text-[var(--ink)] md:hidden" />
                            <LogoMark size={44} className="text-[var(--ink)] hidden md:inline-flex" />
                            <span className="hidden md:flex flex-col leading-none">
                                <span className="font-sans-brand text-[15px] font-bold text-[var(--ink)] tracking-tight">
                                    Zeerow Balance
                                </span>
                                <span className="text-[10px] text-[var(--ink-3)] tracking-wide mt-1 group-hover:text-[var(--accent)] transition-colors">
                                    by Himanshu Tanty
                                </span>
                            </span>
                        </Link>

                        <div className="hidden md:block w-px h-5 bg-[var(--line-2)]" />

                        <nav className="hidden md:flex items-center">
                            {links.map((l) =>
                                renderLink(
                                    l,
                                    'px-4 py-2 rounded-full text-[13px] font-medium text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-white/5 transition-colors'
                                )
                            )}
                        </nav>

                        {/* Resume download — the single primary nav CTA. Contact
                            already lives in the nav links, so this pill focuses
                            on the one thing recruiters actually hunt for. */}
                        {resumeHref && (
                            <a
                                href={resumeHref}
                                target="_blank"
                                rel="noreferrer"
                                download
                                data-testid={PORTFOLIO.navCta}
                                data-cursor="hover"
                                className="hidden sm:flex items-center gap-2 pl-4 pr-2 py-1.5 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-[13px] font-semibold hover:opacity-90 transition-opacity"
                            >
                                <Download size={12} />
                                Resume
                                <span className="hidden md:inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--accent-ink)] text-[var(--accent)]">
                                    <ArrowUpRight size={12} />
                                </span>
                            </a>
                        )}

                        {/* Mobile hamburger — smaller so it fits inside the h-9 pill */}
                        <button
                            type="button"
                            onClick={() => setMobileOpen(true)}
                            aria-label="Open menu"
                            data-testid="nav-mobile-toggle"
                            data-cursor="hover"
                            className="md:hidden inline-flex items-center justify-center w-7 h-7 rounded-full border border-[var(--line-2)] bg-[var(--surface-2)] text-[var(--ink)] hover:border-[var(--accent)] transition-colors"
                        >
                            <Menu size={14} />
                        </button>
                    </div>
                </div>
            </motion.header>

            {/* ───── MOBILE DRAWER ───── */}
            <AnimatePresence>
                {mobileOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.button
                            type="button"
                            aria-label="Close menu"
                            onClick={() => setMobileOpen(false)}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="md:hidden fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm cursor-default"
                        />

                        {/* Drawer panel */}
                        <motion.div
                            key="drawer"
                            role="dialog"
                            aria-modal="true"
                            data-testid="nav-mobile-drawer"
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ duration: 0.42, ease: [0.2, 0.8, 0.2, 1] }}
                            className="md:hidden fixed top-0 right-0 bottom-0 z-[61] w-[86%] max-w-[380px] bg-[var(--bg)] border-l border-[var(--line-2)] flex flex-col overflow-hidden"
                        >
                            {/* dotted texture */}
                            <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0 opacity-[0.3]"
                                style={{
                                    backgroundImage:
                                        'radial-gradient(circle, color-mix(in oklab, var(--ink-3) 45%, transparent) 1px, transparent 1px)',
                                    backgroundSize: '22px 22px',
                                }}
                            />

                            {/* Header */}
                            <div className="relative flex items-center justify-between px-6 pt-6 pb-4">
                                <span className="inline-flex items-center gap-3 font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot" />
                                    <span>Menu</span>
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setMobileOpen(false)}
                                    aria-label="Close menu"
                                    data-testid="nav-mobile-close"
                                    data-cursor="hover"
                                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--line-2)] bg-[var(--surface)] text-[var(--ink)] hover:border-[var(--accent)] transition-colors"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Links */}
                            <nav className="relative flex-1 px-6 py-6 flex flex-col gap-2">
                                {links.map((l, i) => (
                                    <motion.div
                                        key={l.id}
                                        initial={{ opacity: 0, x: 24 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.12 + i * 0.06, duration: 0.5 }}
                                    >
                                        {renderLink(
                                            l,
                                            'group flex items-center justify-between py-4 border-b border-[var(--line)] font-display-italic text-4xl text-[var(--ink)] hover:text-[var(--accent)] transition-colors',
                                            () => setMobileOpen(false)
                                        )}
                                    </motion.div>
                                ))}
                            </nav>

                            {/* Footer CTA */}
                            <div className="relative px-6 pb-8 flex flex-col gap-3">
                                {resumeHref ? (
                                    <a
                                        href={resumeHref}
                                        target="_blank"
                                        rel="noreferrer"
                                        download
                                        data-cursor="hover"
                                        onClick={() => setMobileOpen(false)}
                                        className="inline-flex items-center justify-between gap-2 px-5 py-3 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-sm font-semibold hover:opacity-90 transition-opacity"
                                    >
                                        <span className="inline-flex items-center gap-2">
                                            <Download size={14} />
                                            Resume
                                        </span>
                                        <ArrowUpRight size={14} />
                                    </a>
                                ) : (
                                    <a
                                        href={anchorHref('contact')}
                                        data-cursor="hover"
                                        onClick={() => setMobileOpen(false)}
                                        className="inline-flex items-center justify-between gap-2 px-5 py-3 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-sm font-semibold hover:opacity-90 transition-opacity"
                                    >
                                        Get in touch
                                        <ArrowUpRight size={14} />
                                    </a>
                                )}
                                <p className="mt-3 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)] text-center">
                                    Himanshu Kumar Tanty · IST
                                </p>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navigation;
